import React from 'react';
import { Loader2, BookOpen } from 'lucide-react';

const SalesSourceDocument = ({
    receipt,
    isLoading = false,
    // Optional helpers passed as props to keep the component pure
    formatDate = (date) => new Date(date).toLocaleDateString(),
    formatCurrency = (amount, currency) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
}) => {

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="animate-spin text-indigo-600 mb-4" size={40} />
                <p className="text-slate-500 font-medium tracking-wide">Retrieving Transaction Record...</p>
            </div>
        );
    }

    if (!receipt) return null;

    // const totalPaid = (receipt.totalproduct + (receipt.totaltax || 0)) - (receipt.totaldiscount || 0);
    const calculatedSubtotal = receipt.orderitems?.reduce((sum, item) => sum + (item.totalproduct || 0), 0) || 0;
    const totalPaid = (calculatedSubtotal - (receipt.totaldiscount || 0)) + (receipt.totaltax || 0);

    return (
        <div className="flex flex-col">
            {/* 1. Entity & Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start gap-6 pb-6 border-b border-slate-100">
                <div>
                    <h2 className="text-xs font-black text-indigo-600 uppercase tracking-[0.2em] mb-1">Issuing Organization</h2>
                    <h1 className="text-xl font-bold text-slate-900 leading-tight">
                        {receipt.organization?.organization_name}
                    </h1>
                    <div className="flex items-center gap-2 mt-2 text-slate-500">
                        <BookOpen size={14} />
                        <span className="text-sm font-medium">{receipt.store?.identifier}</span>
                    </div>
                </div>
                <div className="text-right flex flex-col items-end">
                    <div className="bg-slate-900 text-white px-3 py-1 rounded text-[10px] font-bold uppercase tracking-widest mb-2">
                        {receipt.status === 'OUT' ? 'Original Receipt' : receipt.status}
                    </div>
                    <p className="text-xs font-mono text-slate-400">ID: {receipt.idempotency_key}</p>
                    <p className="text-xs font-bold text-slate-700 mt-1">{formatDate(receipt.placed_on)}</p>
                </div>
            </div>

            {/* 2. Customer & Info Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6">
                <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Customer</p>
                    <p className="text-sm font-bold text-slate-800">
                        {receipt.customer?.user?.first_name} {receipt.customer?.user?.last_name}
                    </p>
                </div>
                <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Payment Method</p>
                    <p className="text-sm font-bold text-slate-800 uppercase">{receipt.paymethod || 'N/A'}</p>
                </div>
                <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Currency</p>
                    <p className="text-sm font-bold text-slate-800">{receipt.currency}</p>
                </div>
                <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Order ID</p>
                    <p className="text-sm font-bold text-slate-800">#{receipt.orders_id}</p>
                </div>
            </div>

            {/* 3. Line Items */}
            <div className="border rounded-xl overflow-hidden border-slate-200 shadow-sm">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50 text-[10px] uppercase font-black text-slate-500 tracking-wider">
                            <th className="px-4 py-3">Description</th>
                            <th className="px-4 py-3 text-center">Qty</th>
                            <th className="px-4 py-3 text-right">Unit Price</th>
                            <th className="px-4 py-3 text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {receipt.orderitems?.map((item, idx) => (
                            <tr key={idx} className="text-sm">
                                <td className="px-4 py-3">
                                    <p className="font-bold text-slate-800">{item.catentry?.productname}</p>
                                    <p className="text-[10px] font-mono text-slate-400">{item.catentry?.partnumber}</p>
                                </td>
                                <td className="px-4 py-3 text-center text-slate-600 font-medium">{item.quantity}</td>
                                <td className="px-4 py-3 text-right font-mono text-slate-600">{formatCurrency(item.price, receipt.currency)}</td>
                                <td className="px-4 py-3 text-right font-mono font-bold text-slate-900">{formatCurrency(item.totalproduct, receipt.currency)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* 4. Financial Summary Breakdown */}
            <div className="mt-6 flex flex-col md:flex-row md:justify-end">
                <div className="w-full md:w-72 space-y-2">
                    <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Subtotal</span>
                        <span className="font-mono font-bold text-slate-800">
                            {formatCurrency(calculatedSubtotal, receipt.currency)}
                        </span>
                    </div>

                    {receipt.totaldiscount > 0 && (
                        <div className="flex justify-between text-sm text-rose-600">
                            <span>Discount</span>
                            <span className="font-mono font-bold">-{formatCurrency(receipt.totaldiscount, receipt.currency)}</span>
                        </div>
                    )}

                    <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Tax</span>
                        <span className="font-mono font-bold text-slate-800">{formatCurrency(receipt.totaltax || 0, receipt.currency)}</span>
                    </div>

                    <div className="pt-3 border-t border-slate-200 flex justify-between items-center">
                        <span className="text-xs font-black uppercase text-slate-900">Total Paid</span>
                        <span className="text-xl font-mono font-black text-indigo-700">
                            {formatCurrency(totalPaid, receipt.currency)}
                        </span>
                    </div>
                </div>
            </div>

            {/* 5. Simple Footer */}
            <div className="mt-10 pt-6 border-t border-dashed border-slate-200 text-center">
                <p className="text-[10px] text-slate-400 font-medium uppercase tracking-widest">
                    This is a computer-generated document. No signature required.
                </p>
            </div>
        </div>
    );
};

export default SalesSourceDocument;